/**
 * Alerts on a station's add-on sensors — soil, leaf, extra channels, PM2.5,
 * indoor (backend #219).
 *
 * The backend decides WHICH sensors a station can alert on
 * (`station.alertable_sensors`) and what each one is called. This module holds
 * what it cannot send: how to label a reading, which slider it borrows, and
 * how to render a saved condition whose sensor the station no longer lists.
 *
 * `contract.json` is the backend's `apps/alerts/metrics/contract.json`, copied
 * verbatim — never edit it here. To update it, copy the backend file over this
 * one in its own commit and run the core tests: they are exhaustive over it,
 * so a quantity or family this module cannot label fails them.
 *
 * `messages` is only read inside functions: the mobile tests replace
 * `@smartmixin/i18n` with objects that carry a few namespaces, and a
 * module-scope read would crash every suite that imports core.
 */
import {messages} from '@smartmixin/i18n';
import type {MessageDescriptor} from 'react-intl';

import type {AlertableSensor, WeatherStationFromList} from '../../api/types';
import {resolveGivenSensorName} from '../SensorNames';
import contract from './contract.json';

type FormatMessage = (descriptor: MessageDescriptor) => string;

/** The backend contract this build was made against. */
export const ALERT_CONTRACT_VERSION: string = contract.version;

/** A saved per-sensor metric, as the contract describes it. */
export type SensorMetricInfo = {
  metric: string;
  family: string;
  channel: number | null;
  dimension: string;
  quantity: string;
  name_key: string;
  default_name: string | null;
};

/** Enough to name a sensor: from the payload, or rebuilt from the contract. */
export type ResolvedSensor = {
  family: string;
  channel: number | null;
  name: string | null;
  default_name: string | null;
};

export type QuantityMessages = {
  label: MessageDescriptor;
  lessThan: MessageDescriptor;
  greaterThan: MessageDescriptor;
  lessThanThreshold: MessageDescriptor;
  greaterThanThreshold: MessageDescriptor;
};

/**
 * The global metric whose slider range, step, default and unit handling a
 * reading of each quantity borrows. That is what makes a channel temperature
 * follow the user's °C/°F/K exactly like the station's own T, with no new
 * slider tables.
 */
export const QUANTITY_TEMPLATE_METRIC: Record<string, string> = {
  temperature: 'T',
  humidity: 'U',
  co2: 'CO2',
  pm25: 'PM_2P5',
  aqi: 'AQI',
  soil_moisture: 'U',
  leaf_wetness: 'U',
  // A Netatmo base station's indoor noise (backend !656).
  noise: 'NOISE',
};

/** Labels for a quantity; undefined for one this build does not know. */
export const getQuantityMessages = (
  quantity: string,
): QuantityMessages | undefined => {
  const {alerts, metrics} = messages;
  switch (quantity) {
    case 'temperature':
      return {
        label: metrics.temperature,
        lessThan: alerts.temperatureLessThan,
        greaterThan: alerts.temperatureGreaterThan,
        lessThanThreshold: alerts.temperatureLessThanThreshold,
        greaterThanThreshold: alerts.temperatureGreaterThanThreshold,
      };
    case 'humidity':
      return {
        label: metrics.humidity,
        lessThan: alerts.humidityLessThan,
        greaterThan: alerts.humidityGreaterThan,
        lessThanThreshold: alerts.humidityLessThanThreshold,
        greaterThanThreshold: alerts.humidityGreaterThanThreshold,
      };
    case 'co2':
      return {
        label: metrics.co2,
        lessThan: alerts.co2LessThan,
        greaterThan: alerts.co2GreaterThan,
        lessThanThreshold: alerts.co2LessThanThreshold,
        greaterThanThreshold: alerts.co2GreaterThanThreshold,
      };
    case 'pm25':
      return {
        label: metrics.PM_2P5,
        lessThan: alerts.pm2p5LessThan,
        greaterThan: alerts.pm2p5GreaterThan,
        lessThanThreshold: alerts.pm2p5LessThanThreshold,
        greaterThanThreshold: alerts.pm2p5GreaterThanThreshold,
      };
    case 'aqi':
      return {
        label: metrics.AQI,
        lessThan: alerts.aqiLessThan,
        greaterThan: alerts.aqiGreaterThan,
        lessThanThreshold: alerts.aqiLessThanThreshold,
        greaterThanThreshold: alerts.aqiGreaterThanThreshold,
      };
    case 'soil_moisture':
      return {
        label: metrics.soilMoisture,
        lessThan: alerts.soilMoistureLessThan,
        greaterThan: alerts.soilMoistureGreaterThan,
        lessThanThreshold: alerts.soilMoistureLessThanThreshold,
        greaterThanThreshold: alerts.soilMoistureGreaterThanThreshold,
      };
    case 'leaf_wetness':
      return {
        label: metrics.leafWetness,
        lessThan: alerts.leafWetnessLessThan,
        greaterThan: alerts.leafWetnessGreaterThan,
        lessThanThreshold: alerts.leafWetnessLessThanThreshold,
        greaterThanThreshold: alerts.leafWetnessGreaterThanThreshold,
      };
    case 'noise':
      return {
        label: metrics.noise,
        lessThan: alerts.noiseLessThan,
        greaterThan: alerts.noiseGreaterThan,
        lessThanThreshold: alerts.noiseLessThanThreshold,
        greaterThanThreshold: alerts.noiseGreaterThanThreshold,
      };
    default:
      return undefined;
  }
};

/**
 * A sensor family's label — the dashboard card's own title. Soil-EC shares the
 * soil card's title, so it is marked "(EC)": a station can carry both on the
 * same channel number. Undefined for a family this build does not know: the
 * caller falls back to the sensor's own name.
 */
export const formatSensorFamily = (
  family: string,
  formatMessage: FormatMessage,
): string | undefined => {
  switch (family) {
    // netatmo_module: an additional Netatmo indoor module (backend !656) —
    // titled by its Netatmo name, labelled Indoor like the base station's own
    // indoor readings.
    case 'indoor':
    case 'netatmo_module':
      return formatMessage(messages.common.indoor);
    case 'soil':
      return formatMessage(messages.metrics.soilSensors);
    case 'soil_ec':
      return `${formatMessage(messages.metrics.soilSensors)} (EC)`;
    case 'leaf':
      return formatMessage(messages.metrics.leafSensors);
    case 'temp_humidity':
      return formatMessage(messages.metrics.temperatureSensors);
    case 'temp':
      return formatMessage(messages.metrics.temperatureSensors);
    case 'pm25':
      return formatMessage(messages.airQuality.airQuality);
    default:
      return undefined;
  }
};

const SENSOR_METRICS = new Map<string, SensorMetricInfo>(
  (contract.sensor_metrics as SensorMetricInfo[]).map((entry) => [
    entry.metric,
    entry,
  ]),
);

/** What the contract says about a saved per-sensor metric, if anything. */
export const getSensorMetricInfo = (
  metric: string,
): SensorMetricInfo | undefined => SENSOR_METRICS.get(metric);

/** The picker's value for the station's own (global) metrics. */
export const STATION_SENSOR_KEY = 'station';

export const getSensorKey = (sensor: {
  family: string;
  channel: number | null;
}): string => `${sensor.family}:${sensor.channel ?? ''}`;

/**
 * The station's alertable add-on sensors, minus any reading this build cannot
 * label (a quantity newer than its contract) and any sensor left with none.
 * Empty when the station predates the field, for non-owners, and while the
 * backend rollout flag is off — the picker then looks exactly as it did.
 */
export const getAlertableSensors = (
  station: WeatherStationFromList | undefined,
): AlertableSensor[] =>
  (station?.alertable_sensors ?? [])
    .map((sensor) => {
      const metrics = sensor.metrics.filter((m) =>
        getQuantityMessages(m.quantity),
      );
      return metrics.length === sensor.metrics.length
        ? sensor
        : {...sensor, metrics};
    })
    .filter((sensor) => sensor.metrics.length > 0);

/**
 * The sensor a saved condition's metric belongs to. Prefers the station's
 * served entry; otherwise rebuilds it from the contract and the station's
 * renames — so a condition still reads "Tomato bed: …" with the rollout flag
 * turned back off or the sensor offline. Undefined for a global metric or one
 * this build has never heard of.
 */
export const resolveConditionSensor = (
  metric: string,
  station: WeatherStationFromList | undefined,
): ResolvedSensor | undefined => {
  const listed = (station?.alertable_sensors ?? []).find((sensor) =>
    sensor.metrics.some((m) => m.metric === metric),
  );
  if (listed) {
    return {
      family: listed.family,
      channel: listed.channel,
      name: listed.name,
      default_name: listed.default_name,
    };
  }
  const info = getSensorMetricInfo(metric);
  if (!info) {
    return undefined;
  }
  // The station still lists the sensor, just not this reading: its served
  // name is the only one a Netatmo sensor has — the renames hold no entry
  // for it.
  const sibling = (station?.alertable_sensors ?? []).find(
    (sensor) => getSensorKey(sensor) === getSensorKey(info),
  );
  return {
    family: info.family,
    channel: info.channel,
    name:
      (info.name_key &&
        resolveGivenSensorName(info.name_key, station?.meta?.sensor_names)) ||
      sibling?.name ||
      null,
    default_name: info.default_name,
  };
};

/**
 * The quantity a saved condition's metric measures: from the station's served
 * reading when listed (so a channel newer than this build still labels), else
 * from the contract.
 */
export const getConditionQuantity = (
  metric: string,
  station: WeatherStationFromList | undefined,
): string | undefined => {
  for (const sensor of station?.alertable_sensors ?? []) {
    const reading = sensor.metrics.find((m) => m.metric === metric);
    if (reading) {
      return reading.quantity;
    }
  }
  return getSensorMetricInfo(metric)?.quantity;
};

/** What the notification calls the sensor: its name, else "Ch3", else Indoor. */
export const getSensorDisplayName = (
  sensor: Pick<ResolvedSensor, 'family' | 'name' | 'default_name'>,
  formatMessage: FormatMessage,
): string =>
  sensor.name ||
  sensor.default_name ||
  formatSensorFamily(sensor.family, formatMessage) ||
  sensor.family;

/**
 * A sensor condition as the alert list prints it — "Tomato bed: Soil moisture
 * less than 25% threshold", the Netatmo formatter's "<module>: <sentence>".
 * Kept here so the Help Centre drift test quotes exactly what the app prints.
 */
export const formatSensorConditionLine = (
  sensorName: string,
  sentence: string,
): string => `${sensorName}: ${sentence}`;

/**
 * A row in the sensor picker: the sensor's name, with its family (and default
 * name, when the title does not already show it) underneath. The default name
 * rather than a bare channel number: temp-only and temp+humidity sensors both
 * carry the dashboard title "Temperature & humidity sensors", and "Ch1 T" vs
 * "Ch1 T+H" is what tells two named ones on the same channel apart.
 */
export const getSensorPickerEntry = (
  sensor: ResolvedSensor,
  formatMessage: FormatMessage,
): {title: string; description: string | undefined} => {
  const title = getSensorDisplayName(sensor, formatMessage);
  // An unnamed indoor sensor is already titled "Indoor".
  if (sensor.family === 'indoor' && !sensor.name) {
    return {title, description: undefined};
  }
  const family = formatSensorFamily(sensor.family, formatMessage);
  // A Netatmo module's default name is its alert slot ("Ch1"), which the
  // Netatmo app never shows: it would tell the user nothing.
  const description =
    sensor.name &&
    sensor.default_name &&
    family &&
    sensor.family !== 'netatmo_module'
      ? `${family} · ${sensor.default_name}`
      : family;
  return {title, description};
};
